import type { HomeDestination } from "../hooks/useHomeSurface";

/**
 * The route for a resolved home destination — the pinned room, or the B2C home
 * (the JuisciGPT composer, whose pre-prompt starters are the entry point to the
 * product).
 *
 * WHICH destination is not decided here: `useHomeSurface` owns that, because a
 * room member can be sent to JuisciGPT instead by a per-room GrowthBook rule
 * and the answer is not knowable until the feature payload lands.
 *
 * Shared with the Explore gate's "Not now" exit (`InterestsProfilingPage`): a
 * user who backs out of the profiling conversation must land on the same home
 * the root redirect would have picked, never back on `/discovery` — the gate
 * would bounce them straight into the conversation again.
 */
export function getUserHomeRoute(destination: HomeDestination): string {
  switch (destination.kind) {
    case "pinned-room":
      return `/room/discovery/${destination.roomId}`;
    case "gpt":
      return "/gpt/chat/new";
    default:
      throw new Error(destination satisfies never);
  }
}

/**
 * Where the root path ("/") lands. "Logged out" is keyed on the FIREBASE
 * session (`isAuthenticated`), never on `currentUser === null`: a failed
 * /users/me (API down) also yields null, and treating it as logged out is
 * what fed the /welcome ↔ home redirect ping-pong — the login wall reads the
 * Firebase token and sends the user straight back into the app.
 *
 * `homeDestination` is the SETTLED answer (`useHomeSurface`), so the caller has
 * already held the redirect for as long as it was unknown. Signed-out branches
 * never consult it: nobody's room is knowable there.
 */
export function getInitialRoute({
  currentUser,
  isAuthenticated,
  homeDestination,
}: {
  currentUser: { pinnedRoom: string | null } | null;
  isAuthenticated: boolean;
  homeDestination: HomeDestination;
}): string {
  // `getUserHomeRoute` owns the answer, kept in step with `getUserStartingPage`,
  // which answers the same question for the post-login/notification paths.
  if (currentUser !== null) {
    return getUserHomeRoute(homeDestination);
  }

  // A Firebase session exists but /users/me hasn't resolved (in flight or
  // FAILED — e.g. API down): the user is authenticated, so route into the
  // app rather than the logged-out front door. No user means no pin is
  // knowable, so this is the B2C home whatever the flag says.
  if (isAuthenticated) {
    return getUserHomeRoute({ kind: "gpt" });
  }

  // No session: the welcome door, on every device, whether auth is still
  // loading or done, and for a visitor who signed in on this browser before
  // too. Guessing their home while auth loads would park a logged-out reader
  // on `/gpt/chat/new`, which nothing redirects on desktop; a member who does
  // resolve on `/welcome` is sent home by the router's logged-in guard. (A
  // first boot does not even ask: the Firebase boot gate holds the root until
  // auth resolves.) A logged-out desktop visitor can still open `/gpt/chat/new`
  // and ask there (`GPTAnonymousChatPage`); it is not where they are sent.
  return "/welcome";
}
